import { lazy } from 'react';
import type { ComponentType } from 'react';

/** 上次因为 chunk 加载失败而自动重载的时间戳，用来防重载死循环。 */
const RELOAD_STAMP_KEY = 'aiot-admin:chunk-reload-at';
/** 同一个会话里，两次自动重载至少间隔这么久；更短的连续失败按真故障处理。 */
const RELOAD_COOLDOWN_MS = 30_000;

function readStamp(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0);
  } catch {
    return 0; // 隐私模式/禁用 storage：当作从未重载过，靠下面的 cooldown 也拦不住就让错误冒出来
  }
}

function writeStamp(at: number): void {
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(at));
  } catch {
    /* 忽略 */
  }
}

/**
 * 懒加载页面，并在「chunk 404」时自动重载一次。
 *
 * 前端是带内容 hash 的分包 + GCS 静态托管：发版后 assets 里的 chunk 名全变了，而
 * 已经开着的标签页里跑的还是旧 index 的模块图。这时候点进一个还没加载过的页面/tab，
 * 浏览器会去请求一个已经不存在的文件，报
 *   Failed to fetch dynamically imported module: .../assets/detail-XXXX.js
 * 且因为每个路由/tab 是各自的 chunk，表现为「所有页面偶尔报错」——取决于这个 chunk
 * 之前有没有被加载过。
 *
 * index.html 是 no-cache 的，所以重载一次就能拿到新的模块图。这里只兜前端这一半，
 * 部署侧同时保留旧 chunk（见 Jenkinsfile 的 rsync 注释），让老会话能继续用完当前操作。
 *
 * 只在真的是「模块加载失败」时重载：页面自身抛错（渲染异常、依赖报错）不能靠刷新解决，
 * 刷了只会掩盖问题。
 */
export function lazyPage<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (!isChunkLoadError(err)) throw err;

      const now = Date.now();
      if (now - readStamp() < RELOAD_COOLDOWN_MS) {
        // 刚刚才重载过还是失败 → 不是版本不一致，别把用户困在刷新循环里
        console.error('[lazyPage] chunk 加载失败且刚重载过，放弃自动重载：', err);
        throw err;
      }

      writeStamp(now);
      console.warn('[lazyPage] chunk 已失效（大概率刚发版），自动重载页面：', err);
      window.location.reload();
      // 挂住这个 Promise，避免在页面卸载前先闪一下错误界面
      return new Promise<{ default: T }>(() => {});
    }),
  );
}

/**
 * 是不是动态 import 失败。各浏览器文案不同，用关键词匹配：
 *   Chrome  Failed to fetch dynamically imported module: <url>
 *   Firefox error loading dynamically imported module
 *   Safari  Importing a module script failed.
 */
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    /dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch/i.test(msg)
  );
}
